import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';
import PlatformStartupSettings from './PlatformStartupSettings';
import PlatformRetrySettings from './PlatformRetrySettings';

const SaasPlatformConsole = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [next, setNext] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [licenseDialogMode, setLicenseDialogMode] = useState('activate');
  const [license, setLicense] = useState('');
  const [support, setSupport] = useState(null);
  const [licenseReveal, setLicenseReveal] = useState(null);
  const [licenseRevealBusy, setLicenseRevealBusy] = useState(false);
  const [licenseRevealError, setLicenseRevealError] = useState('');
  const licenseRevealRequest = useRef(0);
  const [storageQuotaEnabled, setStorageQuotaEnabled] = useState(false);
  const [defaultStorageQuotaGb, setDefaultStorageQuotaGb] = useState(10);
  const [quotaDrafts, setQuotaDrafts] = useState({});
  const load = useCallback(async before => {
    try {
      const result = await apiRequest(`/api/v1/saas/platform/organizations?before_id=${before || 0}`);
      setOrganizations(previous => before ? [...previous, ...result.organizations] : result.organizations);
      setStorageQuotaEnabled(Boolean(result.storage_quota_enabled));
      const configuredDefaultGb = Number(result.storage_quota_default_bytes) / (1024 ** 3);
      setDefaultStorageQuotaGb(Number.isFinite(configuredDefaultGb) && configuredDefaultGb > 0 ? configuredDefaultGb : 10);
      setNext(result.next_before_id); setLoaded(true);
    } catch (err) { setError(err.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action, body) => {
    setBusy(true); setError('');
    try {
      await apiRequest(`/api/v1/saas/platform/organizations/${id}/${action}`, { method: 'POST', body: JSON.stringify(body) });
      setSelected(null); setLicense(''); await load();
    } catch (err) {
      const licenseErrorKeys = {
        platform_license_not_assignable: 'saas.error_platform_license_not_assignable',
        invalid_license: 'saas.error_invalid_license',
        license_already_assigned: 'saas.error_license_already_assigned',
        activation_failed: 'saas.error_activation_failed',
      };
      const translationKey = licenseErrorKeys[err.code];
      setError(translationKey ? t(translationKey, { defaultValue: err.message }) : err.message);
    }
    finally { setBusy(false); }
  };
  const inspect = async (id, before) => {
    setBusy(true);
    try {
      const result = await apiRequest(`/api/v1/saas/platform/organizations/${id}/support${before ? `?before_id=${before}` : ''}`);
      setSupport(previous => before ? { ...result, journal: [...previous.journal, ...result.journal] } : result);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const revealLicense = async organization => {
    const requestId = ++licenseRevealRequest.current;
    setLicenseReveal({ organization, license: '', visible: true });
    setLicenseRevealError('');
    setLicenseRevealBusy(true);
    try {
      const result = await apiRequest(`/api/v1/saas/platform/organizations/${organization.id}/license`, { method: 'POST' });
      if (licenseRevealRequest.current === requestId) {
        setLicenseReveal({ organization, license: result.license, visible: true });
      }
    } catch (err) {
      if (licenseRevealRequest.current === requestId) setLicenseRevealError(err.message);
    } finally {
      if (licenseRevealRequest.current === requestId) setLicenseRevealBusy(false);
    }
  };
  const closeLicenseReveal = () => {
    licenseRevealRequest.current += 1;
    setLicenseReveal(null);
    setLicenseRevealError('');
    setLicenseRevealBusy(false);
  };
  const saveStorageQuota = async (organization, useDefault = false) => {
    const id = organization.id;
    const currentQuotaGb = quotaInputValue(organization);
    setBusy(true);
    setError('');
    try {
      await apiRequest(`/api/v1/saas/platform/organizations/${id}/storage-quota`, {
        method: 'POST',
        body: JSON.stringify(useDefault ? { use_default: true } : { quota_gb: currentQuotaGb })
      });
      if (useDefault) {
        setQuotaDrafts(previous => {
          const nextDrafts = { ...previous };
          delete nextDrafts[id];
          return nextDrafts;
        });
      }
      await load();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const formatQuotaGb = bytes => {
    const value = Number(bytes) / (1024 ** 3);
    return Number.isFinite(value) && value > 0 ? value.toFixed(9).replace(/\.?0+$/, '') : String(defaultStorageQuotaGb);
  };
  const quotaInputValue = organization => quotaDrafts[organization.id]
    ?? formatQuotaGb(organization.storage_quota_bytes);
  const canSaveQuota = organization => {
    const value = Number(quotaInputValue(organization));
    return organization.slug !== 'legacy' && Number.isFinite(value) && value > 0;
  };
  return <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
    <Typography variant="h4" gutterBottom>{t('saas.platform_title')}</Typography>
    <Typography color="text.secondary" sx={{ mb: 3 }}>{t('saas.platform_description')}</Typography>
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {loaded && <PlatformStartupSettings />}
    {loaded && <PlatformRetrySettings />}
    {!storageQuotaEnabled && <Alert severity="info" sx={{ mb: 2 }}>{t('saas.storage_quota_disabled')}</Alert>}
    {!loaded && !error && <LinearProgress />}
    <Stack spacing={2}>{organizations.map(org => <Paper key={org.id} variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
        <Box><Typography variant="h6">{org.name}</Typography><Typography color="text.secondary">{t('saas.organization_summary', { count: org.members, credits: org.credits })}</Typography></Box>
        <Chip label={t(`saas.state_${org.status === 'suspended' ? 'suspended' : org.state}`)} />
        {org.activation_requested_at && <Chip color="warning" label={t('saas.activation_pending')} />}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button disabled={busy} onClick={() => inspect(org.id)}>{t('saas.support')}</Button>
          {org.license_assigned && <Button disabled={busy || licenseRevealBusy} onClick={() => revealLicense(org)}>{t('saas.reveal_license')}</Button>}
          {!org.license_assigned && <Button disabled={busy} onClick={() => {
            setSelected(org);
            setLicenseDialogMode('activate');
            setLicense('');
            setError('');
          }}>{t('saas.activate')}</Button>}
          {org.license_assigned && <Button disabled={busy} onClick={() => {
            setSelected(org);
            setLicenseDialogMode('replace');
            setLicense('');
            setError('');
          }}>{t('saas.change_license')}</Button>}
          {org.slug !== 'legacy' && <Button color={org.status === 'active' ? 'warning' : 'primary'} disabled={busy} onClick={() => act(org.id, 'status', { status: org.status === 'active' ? 'suspended' : 'active' })}>{t(org.status === 'active' ? 'saas.suspend' : 'saas.resume')}</Button>}
        </Stack>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
          <TextField
            type="number"
            size="small"
            label={t('saas.storage_quota')}
            value={quotaInputValue(org)}
            onChange={event => setQuotaDrafts(previous => ({ ...previous, [org.id]: event.target.value }))}
            inputProps={{ min: 0.000000001, step: 0.000000001 }}
            disabled={busy || org.slug === 'legacy'}
            sx={{ width: { xs: '100%', sm: 190 } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {org.slug === 'legacy'
              ? t('saas.storage_quota_legacy')
              : org.storage_quota_is_custom
                ? t('saas.storage_quota_custom')
                : t('saas.storage_quota_default', {
                  gb: org.storage_quota_default_bytes
                    ? formatQuotaGb(org.storage_quota_default_bytes)
                    : defaultStorageQuotaGb
                })}
          </Typography>
          <Button
            disabled={busy || !canSaveQuota(org)}
            onClick={() => saveStorageQuota(org)}
          >
            {t('saas.storage_quota_save')}
          </Button>
          {org.slug !== 'legacy' && Boolean(org.storage_quota_is_custom) && <Button disabled={busy} onClick={() => saveStorageQuota(org, true)}>
            {t('saas.storage_quota_reset')}
          </Button>}
        </Stack>
      </Paper>)}</Stack>
    {next && <Button disabled={busy} onClick={() => load(next)} sx={{ mt: 2 }}>{t('saas.load_more')}</Button>}
    <Dialog open={Boolean(selected)} onClose={() => !busy && setSelected(null)} fullWidth maxWidth="sm">
      <DialogTitle>{t(licenseDialogMode === 'replace' ? 'saas.change_license' : 'saas.activate')} · {selected?.name}</DialogTitle>
      <DialogContent>{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ mb: 2 }}>{t(licenseDialogMode === 'replace'
          ? (selected?.status === 'suspended' || selected?.state !== 'active'
            ? 'saas.change_license_reactivate_help'
            : 'saas.change_license_help')
          : 'saas.activation_license_help')}</Typography>
        <TextField autoFocus fullWidth type="password" autoComplete="new-password" label={t('saas.paid_license')} value={license} onChange={e => setLicense(e.target.value)} />
      </DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setSelected(null)}>{t('saas.cancel')}</Button><Button disabled={busy || !license.trim()} variant="contained" onClick={() => act(selected.id, 'activate', { license })}>{t(licenseDialogMode === 'replace' ? 'saas.save_license' : 'saas.activate')}</Button></DialogActions>
    </Dialog>
    <Dialog open={Boolean(licenseReveal)} onClose={closeLicenseReveal} fullWidth maxWidth="sm">
      <DialogTitle>{t('saas.license_reveal_title')} · {licenseReveal?.organization.name}</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>{t('saas.license_reveal_help')}</Alert>
        {licenseRevealError && <Alert severity="error" sx={{ mb: 2 }}>{licenseRevealError}</Alert>}
        {licenseRevealBusy ? <LinearProgress /> : licenseReveal?.license && <TextField
          fullWidth
          label={t('saas.paid_license')}
          type={licenseReveal.visible ? 'text' : 'password'}
          value={licenseReveal.license}
          InputProps={{ readOnly: true }}
        />}
      </DialogContent>
      <DialogActions>
        <Button onClick={closeLicenseReveal}>{t('saas.close')}</Button>
        {licenseReveal?.license && <Button onClick={() => setLicenseReveal(current => ({ ...current, visible: !current.visible }))}>
          {t(licenseReveal.visible ? 'saas.hide_license' : 'saas.reveal_license')}
        </Button>}
      </DialogActions>
    </Dialog>
    <Dialog open={Boolean(support)} onClose={() => setSupport(null)} fullWidth maxWidth="md">
      <DialogTitle>{t('saas.support')} · {support?.organization.name}</DialogTitle>
      <DialogContent><Alert severity="info" sx={{ mb: 2 }}>{t('saas.support_read_only')}</Alert>{support?.journal.map(entry => <Box key={entry.id} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}><Typography>{t(`saas.event_${entry.event}`, { defaultValue: entry.event })}</Typography><Typography variant="caption" color="text.secondary">{new Date(entry.created_at * 1000).toLocaleString()} · {t('saas.actor', { id: entry.actor_id })}</Typography></Box>)}
        {support?.journal.length > 0 && support.journal.length % 50 === 0 && <Button disabled={busy} onClick={() => inspect(support.organization.id, support.journal[support.journal.length - 1].id)}>{t('saas.load_more')}</Button>}
      </DialogContent>
      <DialogActions><Button onClick={() => setSupport(null)}>{t('saas.close')}</Button></DialogActions>
    </Dialog>
  </Box>;
};

export default SaasPlatformConsole;
